import { useMemo } from 'react';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { getShallowNodeValue } from '@spazious/dollhouse-shallow-graph';
import { useGraph, useStaging, useStore } from '@spazious/dollhouse-store';
import { Flex, FlexProps, Select, Typography } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { DollhouseMessage } from '../../core/index.js';
import { useNodesPlacementValidator, useSelectedNodes } from '../../hooks/index.js';
import { useEvents } from '../EventManager/index.js';

import { Grid } from './Grid.js';

export interface EnumFieldProps extends Omit<FlexProps, 'defaultValue'> {
  icon?: React.ReactNode;
  path: string;
  meta?: CommanderActionTypesEnum;
  disabled?: boolean;
  defaultValue?: string;
  options?: {
    value: string;
    label: string;
  }[];
  hasHiddenNodes?: boolean;
  resetsSeating?: boolean;
  onValueChange?: () => void;
}

export const EnumField: React.FC<EnumFieldProps> = ({
  icon,
  path,
  meta = CommanderActionTypesEnum.Param,
  disabled,
  options,
  hasHiddenNodes = false,
  resetsSeating = false,
  onValueChange,
  ...restProps
}) => {
  const { update } = useStaging();
  const staging = useStore(({ staging }) => staging);
  const selectedNodes = useSelectedNodes();
  const events = useEvents();
  const { graph } = useGraph();
  const { validator } = useNodesPlacementValidator(selectedNodes);

  const handleOnChange = useEventCallback((evt: React.ChangeEvent<HTMLSelectElement>) => {
    if (resetsSeating && hasHiddenNodes) {
      events.emit('dollhouseMessage', DollhouseMessage.ConfirmInvisibleNodesReset);
      onValueChange?.();
    } else {
      const idx = parseInt(evt.target.value);
      if (options && Number.isFinite(idx) && idx < options.length) {
        const value = options[idx].value;
        selectedNodes.forEach((node) => update(node.selector!, path, value));
        graph.once('update', () => {
          if (validator()) {
            staging.commit(meta);
          } else {
            staging.abort();
          }
        });
      }
    }
  });

  const value = getShallowNodeValue(selectedNodes[0], path);
  const valueIndex = useMemo(() => {
    return options?.findIndex((option) => option.value === value) ?? 0;
  }, [options, value]);

  const itemNodes = useMemo(
    () =>
      options?.map((option, idx) => (
        <Select.Option key={option.value} value={idx}>
          {option.label}
        </Select.Option>
      )),
    [options]
  );

  return (
    <Grid {...restProps}>
      {icon && (
        <Typography
          size="large"
          style={{
            padding: 8,
            color: 'gray',
            userSelect: 'none',
          }}
          variant="label"
        >
          {icon}
        </Typography>
      )}
      <Flex alignItems="center" minWidth="100px">
        <Select fullWidth disabled={disabled} value={valueIndex} onChange={handleOnChange}>
          {itemNodes}
        </Select>
      </Flex>
    </Grid>
  );
};
